<template>
	<div v-if="value">
		<BaseHeader :title="getTitle" v-if="showHeader" />
		<div class="px-8">
			<div class="flex tr mt-8">
				<div class="md:gap-1 flex-grow items-baseline" :class="{ 'md:mr-6 md:w-1/2': showBank }">
					<div class="flex space-x-4">
						<BaseSelect
							class="w-full"
							v-if="showCompanyRelationshipTypeDropdown && isLevel1Admin"
							ref="BaseSelect.companyRelationshipTypeOptions"
							:label="$t('company_relationship_type')"
							:options="companyRelationshipTypeOptions"
							v-model="selectedCompanyRelationshipType"
							options-label="name"
							track-by="value"
							:required="false"
							:multiple="false"
						/>
						<BaseSelect
							class="w-full"
							v-if="showParentCompaniesDropdown && isLevel1Admin"
							ref="BaseSelect.parentCompanies"
							:label="$t('parent_company')"
							:options="parentCompanies"
							v-model="selectedParentCompany"
							options-label="name"
							track-by="id"
							:disabled="!showParentCompaniesDropdown"
							:required="showParentCompaniesDropdown"
							:multiple="false"
						/>
					</div>
					<div class="flex space-x-4">
						<BaseSelect
							class="w-full"
							v-if="damageAssessorCompanies && showDamageCaseCompaniesDropdown"
							ref="BaseSelect.damageAssessorCompanies"
							:label="$t('damage_assessor_company')"
							:options="damageAssessorCompanies"
							:disable-search="false"
							v-model="selectedDamageAssessorCompanies"
							options-label="name"
							track-by="id"
							:required="false"
							:multiple="true"
							:allow-empty="true"
						/>
						<BaseSelect
							class="w-full"
							v-if="lawyersOfficesOptions && showDamageCaseCompaniesDropdown"
							ref="BaseSelect.kanzlei"
							:label="$t('lawyers_office')"
							:options="lawyersOfficesOptions"
							:disable-search="false"
							v-model="selectedLawyersOffices"
							options-label="name"
							track-by="id"
							:required="false"
							:multiple="true"
							:allow-empty="true"
						/>
						<BaseSelect
							class="w-full"
							v-if="damageAssessorsOptions && showDamageAssessorDropdown"
							ref="BaseSelect.DamageAssessor"
							:label="$t('damage_assessor')"
							:options="damageAssessorsOptions"
							:disable-search="false"
							v-model="selectedDamageAssessors"
							:required="false"
							:multiple="true"
						/>
					</div>
					<BaseInput
						:label="isInsurance ? $t('name_of_insurance') : $t('company')"
						ref="BaseInput.company.name"
						:required="!isDamageAssessorAndSelfEmployed"
						v-show="evaluateShowCompanyNameField"
						type="text"
						v-model="value.name"
					/>
					<div class="flex space-x-4">
						<BaseInput
							class="w-1/2"
							:label="$t('tax_number')"
							ref="BaseInput.company.taxNumber"
							type="text"
							:required="!value.sales_tax || (value.sales_tax && value.taxNumber)"
							v-model="value.taxNumber"
							v-if="!isInsurance"
						/>
						<BaseInput
							class="w-1/2"
							:label="$t('ust_id')"
							ref="BaseInput.company.sales_tax"
							type="text"
							:required="!value.taxNumber || (value.sales_tax && value.taxNumber)"
							v-model="value.sales_tax"
							v-if="!isInsurance && (isLawyer || isUserAllowedInLevel3)"
						/>
					</div>
					<div class="flex space-x-4" v-if="!isInsurance">
						<BaseInput
							class="w-1/2"
							:label="$t('owner_first_name')"
							ref="BaseInput.company.vorname"
							:required="true"
							type="text"
							v-model="value.ownerFirstname"
						/>
						<BaseInput
							class="w-1/2"
							:label="$t('owner_last_name')"
							ref="BaseInput.company.nachname"
							:required="true"
							type="text"
							v-model="value.ownerLastname"
						/>
					</div>
					<div class="flex" v-if="isInsurance">
						<CleanBaseRadio
							v-for="option in postalOptions"
							:value="option.value"
							:label="option.display"
							:form-value="selectedPostalOption"
							:key="option.display"
							@click="setSelectedPostal($event)"
							:class="option.class"
							class="pt-2 pb-4"
						/>
					</div>
					<div class="flex" v-if="!isInsurance || selectedPostalOption === postalOptions[0].value">
						<BaseInput :label="$t('street')" ref="BaseInput.company.street" :required="true" type="text" v-model="value.street" class="w-full mr-4" />
						<BaseInput :label="$t('number')" ref="BaseInput.company.houseNumber" :required="false" type="tel" v-model="value.houseNumber" />
					</div>
					<div v-else>
						<BaseInput :label="$t('po_box')" ref="BaseInput.company.poBox" :required="true" type="text" v-model="value.poBox" class="w-full mr-4" />
					</div>
					<CityInput ref="CityInput" v-model="value.city" />
					<div class="flex w-full space-x-4">
						<BaseInput
							class="w-full"
							:label="$t('phone')"
							ref="BaseInput.company.phoneNumber"
							validationtype="phone"
							:required="true"
							type="tel"
							v-model="value.phoneNumber"
						/>
						<BaseInput
							class="w-full"
							:label="$t('mobile_number')"
							ref="BaseInput.company.mobileNumber"
							validationtype="phone"
							type="tel"
							v-if="!isInsurance && !isLawyer && !isUserAllowedInLevel3"
							v-model="value.mobileNumber"
						/>
					</div>
					<BaseInput
						:label="$t('fax')"
						ref="BaseInput.company.fax"
						validationtype="phone"
						type="text"
						v-model="value.fax"
						v-if="isLawyer || isUserAllowedInLevel3"
					/>
					<BaseInput
						:label="$t('first_e_mail_address')"
						ref="BaseInput.company.email"
						validationtype="email"
						:required="true"
						type="email"
						v-model="value.email"
					/>
					<BaseInput
						:label="$t('second_e_mail_address')"
						ref="BaseInput.company.secondEmail"
						validationtype="email"
						:required="false"
						type="email"
						v-model="value.secondEmail"
						v-if="!isInsurance && !isLawyer && !isUserAllowedInLevel3"
					/>
					<BaseInput :label="$t('homepage')" ref="BaseInput.company.homepage" validationtype="domain" type="text" v-model="value.homepage" />
					<BaseInput
						:label="$t('company_head_quarter')"
						ref="BaseInput.company.firmensitz"
						:required="false"
						type="text"
						v-model="value.firmensitz"
						v-if="!isInsurance && !isLawyer && !isUserAllowedInLevel3"
					/>
					<BaseInput
						:label="$t('district_court')"
						ref="BaseInput.company.amtsgericht"
						:required="false"
						type="text"
						v-model="value.amtsgericht"
						v-if="!isInsurance && !isLawyer && !isUserAllowedInLevel3"
					/>
					<div class="flex w-full space-x-4">
						<BaseSelect
							ref="BaseInput.company.hr"
							class="w-1/2"
							:options="hrOptions"
							v-if="!isInsurance && !isLawyer && !isUserAllowedInLevel3"
							:label="$t('trading_office')"
							v-model="selectedHrOption"
						/>
						<BaseInput
							:label="$t('number')"
							ref="BaseInput.company.hr_number"
							:required="false"
							type="text"
							v-model="value.hr_number"
							class="w-1/2"
							v-if="!isInsurance && !isLawyer && !isUserAllowedInLevel3"
						/>
					</div>
				</div>
				<div class="md:gap-1 flex-grow items-baseline md:w-1/2" v-if="showBank">
					<BaseInput :label="$t('name_of_bank')" ref="BaseInput.company.bankAccount.bank" type="text" v-model="value.bankAccount.bank" />
					<BaseInput
						:label="$t('iban')"
						ref="BaseInput.company.bankAccount.iban"
						type="tel"
						mask="AANN NNNN NNNN NNNN NNNN NNNN NNNN NNNN NN"
						v-model="value.bankAccount.iban"
					/>
					<BaseInput :label="$t('bic')" ref="BaseInput.company.bankAccount.bic" type="text" v-model="value.bankAccount.bic" />
					<div style="text-align: start; font-size: 14px;" class="font-bold" v-if="isUserAllowedInLevel5or6">
						{{ $t('bank_data_change_hint') }}
					</div>
				</div>
			</div>

			<div
				v-if="showCompanyVariables || showSign"
				class="flex flex-col w-full pr-0 gap-x-8 xl:gap-x-24 gap-y-4 border-t-2 pb-5 mt-3 mb-5 border-connectProBlue"
			>
				<div
					class="pt-2 pa-0 mt-5 justify-center md:gap-1 flex-grow items-baseline md:w-1/2"
					style="align-self: start; align-content: start"
					v-if="showSign"
				>
					<div class="flex mb-6">
						<div class="font-bold mr-5" style="color: #495057">
							{{ $t('sign_stamp') }}
						</div>
						<div
							v-if="getAuthenticatedUserGroupLevel !== '1'"
							class="flex rounded-full h-6 w-6 justify-center cursor-pointer tooltip"
							style="background-color: lightgrey; align-items: center"
						>
							<font-awesome-icon :icon="['fas', 'info']" class="flex justify-center text-connectProBlue" style="align-content: center" />
							<div class="tooltiptext">
								{{ $t('admin_checks_file_two_days') }}
							</div>
						</div>
						<div class="ml-5 mt-1 parent" v-if="value.companySign != null">
							<font-awesome-icon
								:icon="value.signUnblockedByAdmin ? ['fas', 'check'] : ['fas', 'question']"
								alt="hhs"
								class="flex justify-center text-connectProBlue cursor-pointer"
								style="align-content: center"
							/>
							<div class="hidden absolute rounded p-2 h-4 mx-1 text-white parent-hover:show">
								{{ value.signUnblockedByAdmin ? $t('unblocked') : $t('needs_check') }}
							</div>
						</div>
					</div>
					<div class="flex gap-3">
						<BaseButton
							v-if="checkAdminForAssets && companySign != null"
							@click="downloadAsset(false)"
							class="w-full sm:w-auto mt-6 sm:mt-0 justify-center flex"
							style="align-self: start"
						>
							<font-awesome-icon :icon="['fas', 'download']" class="w-5 h-5 pr-1 pt-1 justify-center" size="lg" />
							{{ $t('download') }}
						</BaseButton>
						<BaseButton @click="openUpload" class="w-full sm:w-auto mt-6 sm:mt-0 justify-center flex" style="align-self: start">
							<input type="file" ref="sign" accept="image/png, image/jpg, image/bmp, image/jpeg" @change="chosenFile(true)" class="hidden" />
							<font-awesome-icon :icon="['fas', 'image']" class="w-5 h-5 pr-1 pt-1 justify-center" size="lg" />
							{{ isUserAllowedInLevel1 ? $t('upload_and_unblock') : $t('pick_file') }}
						</BaseButton>
					</div>
					<BaseSign
						class="justify-start"
						:background-image="companySign"
						ref="BaseInput.company.signpad"
						:align-left="true"
						v-model="companySign"
						@clear="
							companySign = null;
							$store.commit('setConfirmLeaving', $t('leave_page'));
						"
					/>
				</div>
				<div v-if="showSign" class="border-b-2 mt-5 border-connectProBlue"></div>
				<div class="md:gap-1 grid grid-cols-2 justify-start">
					<div
						class="pt-2 pa-0 mt-5 justify-center md:gap-1 flex-grow items-baseline md:w-full"
						style="align-self: start; align-content: start"
						v-if="showCompanyVariables"
					>
						<div class="flex mb-6">
							<div class="font-bold mr-5" style="color: #495057">
								{{ $t('company_logo') }}
							</div>
							<div class="flex rounded-full h-6 w-6 justify-center cursor-pointer tooltip" style="background-color: lightgrey; align-items: center">
								<font-awesome-icon :icon="['fas', 'info']" class="flex justify-center text-connectProBlue" style="align-content: center" />
								<div class="tooltiptext">
									{{ isUserAllowedInLevel1 ? $t('logo_hint') : $t('admin_cuts_file_two_days') }}
								</div>
							</div>
							<div class="ml-5 mt-1 parent" v-if="value.companyLogo != null">
								<font-awesome-icon
									:icon="value.logoUnblockedByAdmin ? ['fas', 'check'] : ['fas', 'question']"
									alt="hhs"
									class="flex justify-center text-connectProBlue cursor-pointer"
									style="align-content: center"
								/>
								<div class="hidden absolute rounded p-2 h-4 mx-1 text-white parent-hover:show">
									{{ value.logoUnblockedByAdmin ? $t('unblocked') : $t('needs_check') }}
								</div>
							</div>
						</div>
						<div class="flex gap-3">
							<BaseButton
								v-if="checkAdminForAssets && companyLogo != null"
								@click="downloadAsset(true)"
								class="w-full sm:w-auto mt-6 sm:mt-0 justify-center flex"
								style="align-self: start"
							>
								<font-awesome-icon :icon="['fas', 'download']" class="w-5 h-5 pr-1 pt-1 justify-center" size="lg" />
								{{ $t('download') }}
							</BaseButton>
							<BaseButton @click="openUpload('logo')" class="w-full sm:w-auto mt-6 sm:mt-0 justify-center flex" style="align-self: start">
								<input type="file" ref="file" accept="image/png, image/jpg, image/bmp, image/jpeg" @change="chosenLogo(true)" class="hidden" />
								<font-awesome-icon :icon="['fas', 'image']" class="w-5 h-5 pr-1 pt-1 justify-center" size="lg" />
								{{ isUserAllowedInLevel1 ? $t('upload_and_unblock') : $t('pick_file') }}
							</BaseButton>
						</div>

						<div class="w-full flex flex-shrink justify-start mt-4 mb-4">
							<div
								v-if="companyLogo"
								class="flex mb-4 justify-start w-full"
								style="width: fit-content"
								@mouseover="hover = true"
								@mouseleave="hover = false"
							>
								<img
									alt=" "
									:src="companyLogo"
									@load="isLoaded = true"
									class="justify-center w-full"
									style="align-content: center; text-align: center; object-fit: fill; background: #e4e5e4;"
								/>
							</div>
							<div v-else class="bg-gray-300" style=" width: 200px; height: 117px" />
						</div>
						<div class="w-full flex justify-start">
							<BaseButton
								@click="
									companyLogo = null;
									$store.commit('setConfirmLeaving', $t('leave_page'));
								"
								>{{ $t('delete') }}</BaseButton
							>
						</div>
						<div>
							<BaseSelect
								class="w-1/2 mt-6"
								ref="BaseSelect.logoPosition"
								:label="$t('logo_position_description')"
								:options="logoPositionOptions"
								v-model="selectedLogoPosition"
								options-label="display"
								track-by="value"
								:required="false"
								:multiple="false"
								font="normal"
							/>
						</div>
					</div>
					<div
						class="pt-2 ml-6 mt-5 justify-start md:gap-1 pr-8 flex-grow items-baseline md:w-full"
						style="align-self: start"
						v-if="showCompanyVariables"
					>
						<div class="flex mb-6">
							<div class="font-bold mr-5" style="color: #495057">
								{{ $t('letter_header') }}
							</div>
							<div class="flex rounded-full h-6 w-6 justify-center cursor-pointer tooltip" style="background-color: lightgrey; align-items: center">
								<font-awesome-icon :icon="['fas', 'info']" class="flex justify-center text-connectProBlue" style="align-content: center" />
								<div class="tooltiptext">
									{{ isUserAllowedInLevel1 ? $t('letter_header_hint') : $t('admin_cuts_letter_header_two_days') }}
								</div>
							</div>
							<div class="ml-5 mt-1 parent" v-if="value.letterHeader != null">
								<font-awesome-icon
									:icon="value.letterHeaderUnblockedByAdmin ? ['fas', 'check'] : ['fas', 'question']"
									class="flex justify-center text-connectProBlue cursor-pointer"
									style="align-content: center"
								/>
								<div class="hidden absolute rounded p-2 h-4 mx-1 text-white parent-hover:show">
									{{ value.letterHeaderUnblockedByAdmin ? $t('unblocked') : $t('needs_check') }}
								</div>
							</div>
						</div>
						<div class="flex gap-3">
							<BaseButton
								v-if="checkAdminForAssets && letterHeader != null"
								@click="downloadLetterHeader"
								class="w-full sm:w-auto mt-6 sm:mt-0 justify-center flex"
								style="align-self: start"
							>
								<font-awesome-icon :icon="['fas', 'download']" class="w-5 h-5 pr-1 pt-1 justify-start" size="lg" />
								{{ $t('download') }}
							</BaseButton>
							<BaseButton @click="openUpload('letterHeader')" class="w-full sm:w-auto mt-6 sm:mt-0 justify-center flex" style="align-self: start">
								<input type="file" ref="letter" accept="image/png, image/jpg, image/bmp, image/jpeg" @change="chosenFile(false)" class="hidden" />
								<font-awesome-icon :icon="['fas', 'image']" class="w-5 h-5 pr-1 pt-1 justify-start" size="lg" />
								{{ isUserAllowedInLevel1 ? $t('upload_and_unblock') : $t('pick_file') }}
							</BaseButton>
						</div>
						<div class="w-full flex justify-start mt-4 mb-4">
							<div
								v-if="letterHeader"
								class="flex mb-4 justify-start w-full"
								style="width: fit-content"
								@mouseover="hoverLetter = true"
								@mouseleave="hoverLetter = false"
							>
								<img
									alt=" "
									:src="letterHeader"
									@load="isLoaded = true"
									class="justify-center w-full"
									style="align-content: center; text-align: center; object-fit: fill; background: #e4e5e4;"
								/>
							</div>
							<div v-else class="bg-gray-300" style="width: 200px; height: 117px" />
						</div>
						<div class="w-full flex justify-start">
							<BaseButton
								@click="
									letterHeader = null;
									$store.commit('setConfirmLeaving', $t('leave_page'));
								"
								>{{ $t('delete') }}</BaseButton
							>
						</div>
					</div>
				</div>
			</div>
		</div>
		<Footer
			class="mx-8"
			:is-back-button="false"
			:class="{
				'justify-end': !isEditModus || !(getAuthenticatedUserGroupLevel === '5' || getAuthenticatedUserGroupLevel === '6'),
				'justify-between': isEditModus && (getAuthenticatedUserGroupLevel === '5' || getAuthenticatedUserGroupLevel === '6'),
			}"
		>
			<template #backButton>
				<BaseButton @click="$router.go(-1)" v-if="(!isUserAllowedInLevel1 && !isAuthenticatedUserInGroupAdmin) || isInsurance">
					{{ $t('general.back') }}
				</BaseButton>
			</template>

			<template #right>
				<slot name="buttons" />
			</template>
			<template #continueButton>
				<SaveButton @click="save" class="w-16 btn-blue" :disabled="isLoading">
					{{ isEditModus || isInsurance ? $t('general.save') : $t('general.next') }}
				</SaveButton>
			</template>
		</Footer>
	</div>
</template>

<script>
import BaseHeader from '@/components/navigation/BaseHeader';
import BaseInput from '@/components/forms/inputs/BaseInput';
import BaseButton from '@/components/forms/buttons/BaseButton';
import BaseSign from '@/components/forms/inputs/BaseSign';
import Validate from '@/mixins/Validate';
import { CompanyApiFactory, DamageassessorsApiFactory, DamageassessorscompanyApiFactory } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import { _regxp, concatArray } from '@/plugins/Utils';
import CityInput from '@/components/forms/CityInput';
import BaseSelect from '@/components/forms/inputs/BaseSelect';
import Authentication from '@/mixins/Authentication';
import LawyerOfficeRepository from '@/api/repositories/LawyerOfficeRepository';
import SaveButton from '@/components/forms/buttons/SaveButton';
import Footer from '@/components/damagecases/Footer';
import CleanBaseRadio from '@/components/forms/inputs/CleanBaseRadio.vue';

export default {
	components: { CleanBaseRadio, Footer, SaveButton, BaseSelect, CityInput, BaseSign, BaseInput, BaseHeader, BaseButton },
	mixins: [Validate, Authentication],
	name: 'CompanyProfileForm',
	props: {
		sendToApi: { default: true },
		value: {
			companySign: null,
		},
		isEditModus: { type: Boolean, default: false },
		companyType: {},
		type: {},
		showHeader: { default: true },
	},
	computed: {
		showCompanyVariables() {
			return (
				((this.getAuthenticatedUserGroupLevel === '5' ||
					this.getAuthenticatedUserGroupLevel === '6' ||
					this.getAuthenticatedUserGroupLevel === '3' ||
					this.getAuthenticatedUserGroupLevel === '2') &&
					this.isAuthenticatedUserInGroupAdmin) ||
				(this.getAuthenticatedUserGroupLevel === '1' &&
					(this.getType === 'car_dealership' ||
						this.getType === 'repairShop' ||
						this.getType === 'lawyerOffice' ||
						this.getType === 'damageassessor') &&
					this.isEditModus)
			);
		},
		checkAdminForAssets() {
			return this.isUserAllowedInLevel1 && this.isEditModus;
		},
		getType() {
			return this.type || this.$route.params.type;
		},
		getTitle() {
			return this.createTitle();
		},
		evaluateShowCompanyNameField() {
			if (this.getType === 'damageassessor') {
				if (!this.isEditModus) {
					return true;
				} else {
					if (this.value.companyable === undefined) {
						return true;
					} else {
						return this.value.companyable.self_employed === 0;
					}
				}
			} else return true;
		},
		isDamageAssessorAndSelfEmployed() {
			if (this.isEditModus) {
				if (this.value.companyable === undefined) {
					return false;
				} else {
					return this.value.companyable.self_employed === 1;
				}
			} else {
				return this.getType === 'damageassessor';
			}
		},
		isInsurance() {
			return this.getType === 'insurance';
		},
		isLawyer() {
			return this.getType === 'lawyerOffice';
		},
		getAuthenticatedUserGroupLevel() {
			return this.$store.getters.getAuthenticatedUserGroupLevel;
		},
		showBank() {
			return this.getType !== 'insurance' && this.getType !== 'car_dealership' && this.getType !== 'repairShop';
		},
		showDamageAssessorDropdown() {
			const type = this.getType;
			return type === 'repairShop' || type === 'car_dealership' || type === 'insuranceoffice';
		},
		showCompanyRelationshipTypeDropdown() {
			const type = this.getType;
			return type === 'repairShop' || type === 'car_dealership';
		},
		showParentCompaniesDropdown() {
			const type = this.selectedCompanyRelationshipType.value;
			switch (type) {
				case 'child':
					return true;
				case 'parent':
					return false;
				case 'solo':
					return false;
				default:
					return false;
			}
		},
		showDamageCaseCompaniesDropdown() {
			const type = this.getType;
			const isCorrectType = type === 'repairShop' || type === 'car_dealership';
			return isCorrectType && this.isUserAllowedInLevel1;
		},
		showSign() {
			let show = false;
			if ((this.getAuthenticatedUserGroupLevel === '1' || this.getAuthenticatedUserGroupLevel === '2') && this.isEditModus) {
				show = true;
			}
			if (this.getType) {
				if (this.getType !== 'damageassessor') {
					show = false;
				}
			}
			return show;
		},
	},
	data() {
		return {
			selectedDamageAssessors: null,
			damageAssessors: null,
			damageAssessorsOptions: null,

			selectedDamageAssessorCompanies: null,
			damageAssessorCompanies: null,

			lawyersOfficesOptions: null,
			selectedLawyersOffices: null,

			doneInitializing: false,
			regxp: _regxp(),
			companySign: null,
			companyLogo: null,
			letterHeader: null,
			isLoaded: false,
			hover: false,
			hoverLetter: false,
			isLoading: false,
			hrOptions: [
				{ display: 'HRB', value: 'hrb' },
				{ display: 'HRA', value: 'hra' },
			],
			selectedHrOption: null,
			logoPositionOptions: [
				{ display: 'Links', value: 'left' },
				{ display: 'Mittig', value: 'center' },
				{ display: 'Rechts', value: 'right' },
			],
			selectedLogoPosition: null,
			filePickerOpen: false,

			companyRelationshipTypeOptions: [
				{ name: 'Eigenständiges Unternehmen', value: 'solo' },
				{ name: 'Hauptsitz (mit Niederlassungen/Filialen)', value: 'parent' },
				{ name: 'Niederlassung/Filiale', value: 'child' },
			],
			selectedCompanyRelationshipType: { name: 'Eigenständiges Unternehmen', value: 'solo' },

			selectedParentCompany: null,
			parentCompanies: [],
			postalOptions: [
				{
					display: 'Straße',
					value: 0,
				},
				{
					display: 'Postfach',
					value: 1,
				},
			],
			selectedPostalOption: null,
		};
	},
	mounted() {
		this.loadDamageAssessors();
		this.value.poBox === null || this.value.poBox === undefined ? this.setSelectedPostal(0) : this.setSelectedPostal(1);
		if (this.isEditModus) {
			this.getCompanyRelationshipType();
			if (this.value.hr) {
				this.selectedHrOption = this.hrOptions.find(option => option.value === this.value.hr);
			}

			this.selectedLogoPosition = this.logoPositionOptions.find(option => option.value === this.value.logoPosition);
			if (this.checkCompanyTypeAllowance() || this.isLevel1Admin) {
				this.companyLogo = this.value.companyLogo;
				this.companySign = this.value.companySign;
				this.letterHeader = this.value.letterHeader;
			}
		}

		if (this.showParentCompaniesDropdown) {
			this.getAllParentCompanies();
		}
		this.doneInitializing = true;
		if (!this.isEditModus) {
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
		}
	},
	methods: {
		setSelectedPostal(event) {
			this.selectedPostalOption = event;
		},
		loadDamageAssessors() {
			if (this.showDamageCaseCompaniesDropdown) {
				this.getAllDamageAssessorsCompanies();
				this.getLawyersOffices();
			} else if (this.showDamageAssessorDropdown) {
				this.getAllDamageAssessors();
			}
		},
		downloadAsset(isLogo) {
			const a = document.createElement('a');
			a.href = this.companySign;
			if (isLogo) {
				a.download = `${this.value.name}_logo.png`;
			} else {
				a.download = `${this.value.name}_sign.png`;
			}

			a.click();
		},
		downloadLetterHeader() {
			const a = document.createElement('a');
			a.href = this.letterHeader;
			a.download = `${this.value.name}_briefkopf.png`;
			a.click();
		},
		createTitle() {
			if (!this.isEditModus) {
				return this.companyType + ' hinzufügen';
			}
			return this.companyType + ' bearbeiten';
		},
		getAllDamageAssessorsCompanies() {
			return DamageassessorscompanyApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damageassessorscompanyGet()
				.then(response => {
					this.damageAssessorCompanies = response.data;
					if (this.value.damageAssessorCompanies) {
						this.selectedDamageAssessorCompanies = this.damageAssessorCompanies.filter(value =>
							this.value.damageAssessorCompanies.find(selected => selected.id === value.id),
						);
					}
				});
		},
		getAllParentCompanies() {
			const type = this.getType;
			return CompanyApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.parentCompaniesGet(type)
				.then(response => {
					this.parentCompanies = response.data;
					if (this.value.parent_company_id !== null) {
						this.selectedParentCompany = this.parentCompanies.find(company => company.id === this.value.parent_company_id);
					}
				});
		},
		getLawyersOffices() {
			LawyerOfficeRepository.index().then(response => {
				this.lawyersOfficesOptions = response.data;
				if (this.value.lawyerOffices) {
					this.selectedLawyersOffices = this.lawyersOfficesOptions.filter(value =>
						this.value.lawyerOffices.find(selected => selected.id === value.id),
					);
				}
			});
		},
		getAllDamageAssessors() {
			return DamageassessorsApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damageassessorsGet(undefined, undefined, true)
				.then(response => {
					this.damageAssessors = response.data;
					this.damageAssessorsOptions = this.damageAssessors.map(option => {
						if (option.person !== null) {
							option.value = option.id;
							option.display = concatArray([option.person.firstName, option.person.lastName]);
							return option;
						}
					});

					if (this.value.damageAssessors) {
						this.selectedDamageAssessors = this.damageAssessorsOptions.filter(value =>
							this.value.damageAssessors.find(selected => selected.id === value.id),
						);
					}
				});
		},
		openUpload(type) {
			if (!this.filePickerOpen) {
				switch (type) {
					case 'logo':
						{
							this.$swal(this.$t('general.hint').toString(), this.$t('logo_hint').toString(), 'info').then(alert => {
								this.filePickerOpen = true;
								this.$refs.file.click();
								this.filePickerOpen = false;
							});
						}
						break;
					case 'letterHeader':
						{
							this.$swal(this.$t('general.hint').toString(), this.$t('letter_header_hint').toString(), 'info').then(alert => {
								this.filePickerOpen = true;
								this.$refs.letter.click();
								this.filePickerOpen = false;
							});
						}
						break;
					default: {
						this.filePickerOpen = true;
						this.$refs.sign.click();
						this.filePickerOpen = false;
					}
				}
			}
		},
		chosenFile(isSign) {
			this.filePickerOpen = false;
			let file;
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
			if (isSign) {
				file = this.$refs.sign.files[0];
				if (!this.isUserAllowedInLevel1) {
					this.value.signUnblockedByAdmin = false;
				}
			} else {
				file = this.$refs.letter.files[0];
				if (!this.isUserAllowedInLevel1) {
					this.value.letterHeaderUnblockedByAdmin = false;
				}
			}

			const reader = new FileReader();

			reader.onload = e => {
				if (isSign) {
					this.companySign = e.target.result;
				} else {
					this.letterHeader = e.target.result;
				}
			};

			reader.readAsDataURL(file);
			this.showAdminUnblockPrompt(isSign ? 'sign' : 'letterHeader');
		},
		chosenLogo() {
			this.filePickerOpen = false;
			const oldImage = this.companyLogo;
			const file = this.$refs.file.files[0];
			const reader = new FileReader();
			this.$store.commit('setConfirmLeaving', this.$t('leave_page'));
			if (!this.isUserAllowedInLevel1) {
				this.value.logoUnblockedByAdmin = false;
			}

			if (!file.type.startsWith('image/')) {
				return false;
			}

			const i = new Image();
			// eslint-disable-next-line @typescript-eslint/no-this-alias
			const context = this;

			i.onload = function() {
				if (i.height < 500 && i.width < 500) {
					context.$swal(context.$t('general.attention').toString(), context.$t('upload_in_better_quality').toString(), 'warning');
					context.companyLogo = oldImage;
				}
			};

			reader.onload = e => {
				this.companyLogo = e.target.result;
				i.src = this.companyLogo;
			};

			reader.readAsDataURL(file);
			this.showAdminUnblockPrompt('logo');
		},
		showAdminUnblockPrompt(type) {
			let typeString = '';
			switch (type) {
				case 'logo':
					typeString = 'das Firmenlogo';
					break;
				case 'letterHeader':
					typeString = 'den Briefkopf';
					break;
				case 'sign':
					typeString = 'die Signatur';
					break;
			}
			if (this.isUserAllowedInLevel1) {
				this.$swal({
					title: this.$t('general.attention').toString(),
					text: this.$t('admin_unblock_message', { type: typeString }).toString(),
					icon: 'info',
					cancelButtonText: this.$t('later').toString(),
					showCancelButton: true,
					confirmButtonText: this.$t('upload_and_unblock').toString(),
				}).then(result => {
					switch (type) {
						case 'logo':
							this.value.logoUnblockedByAdmin = result.value;
							break;
						case 'letterHeader':
							this.value.letterHeaderUnblockedByAdmin = result.value;
							break;
						case 'sign':
							this.value.signUnblockedByAdmin = result.value;
							break;
					}
				});
			}
		},
		checkCompanyTypeAllowance() {
			return (
				(this.getAuthenticatedUserGroupLevel === '5' ||
					this.getAuthenticatedUserGroupLevel === '6' ||
					this.getAuthenticatedUserGroupLevel === '3' ||
					this.getAuthenticatedUserGroupLevel === '2') &&
				this.isAuthenticatedUserInGroupAdmin
			);
		},
		getCompanyRelationshipType() {
			switch (this.value.company_relationship_type) {
				case 'child':
					this.selectedCompanyRelationshipType = this.companyRelationshipTypeOptions[2];
					this.isChildCompany = true;
					break;
				case 'parent':
					this.selectedCompanyRelationshipType = this.companyRelationshipTypeOptions[1];
					this.isChildCompany = false;
					break;
				case 'solo':
					this.selectedCompanyRelationshipType = this.companyRelationshipTypeOptions[0];
					this.isChildCompany = false;
					break;
			}
		},
		save() {
			if (this.validate()) {
				this.isLoading = true;
				this.value.damageAssessors = this.selectedDamageAssessors;
				this.value.damageAssessorCompanies = this.selectedDamageAssessorCompanies;
				this.value.lawyerOffices = this.selectedLawyersOffices;
				this.value.agent = this.selectedAgent;
				if (this.value.iban) {
					this.value.bankAccount.iban = this.value.bankAccount.iban.replaceAll(' ', '');
				}
				if (this.selectedHrOption) {
					this.value.hr = this.selectedHrOption.value;
				}
				if (this.selectedPostalOption === 1) {
					this.value.street = null;
					this.value.houseNumber = null;
				} else {
					this.value.poBox = null;
				}
				this.value.company_relationship_type = this.selectedCompanyRelationshipType.value;
				if (this.selectedParentCompany && this.showParentCompaniesDropdown) {
					this.value.parent_company_id = this.selectedParentCompany.id;
				}

				if (this.$route.meta.action === 'new') {
					this.$emit('save', this.value);
					this.isLoading = false;
				} else if (this.sendToApi) {
					if (this.checkCompanyTypeAllowance() || this.isLevel1Admin) {
						this.value.companyLogo = this.companyLogo;
						this.value.letterHeader = this.letterHeader;
						this.value.logoPosition = this.selectedLogoPosition.value;
						this.value.companySign = this.companySign;
						// this.value.logoUnblockedByAdmin = this.logoUnblockedByAdmin;
						// this.value.signUnblockedByAdmin = this.signUnblockedByAdmin;
						// this.value.letterHeaderUnblockedByAdmin = this.letterHeaderUnblockedByAdmin;
					}
					CompanyApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
						.companyIdPut(this.value.id, this.value)
						.then(response => {
							this.isLoading = false;
							this.$swal({
								title: this.$t('general.success').toString(),
								text: this.$t('changed_data_successfully').toString(),
								icon: 'success',
							});
							this.$store.commit('setConfirmLeaving', false);
						})
						.catch(error => {
							this.isLoading = false;
							this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
						});
				} else {
					this.$emit('save', this.value);
					this.isLoading = false;
				}
			} else {
				this.$swal(this.$t('general.attention').toString(), this.$t('please_check_input').toString(), 'warning');
			}
		},
	},
	watch: {
		'value.companyLogo': function(oldValue) {
			if (this.checkCompanyTypeAllowance() || this.isLevel1Admin) {
				this.companyLogo = this.value.companyLogo;
			}
		},
		'value.companySign': function(oldValue) {
			if (this.checkCompanyTypeAllowance() || this.isLevel1Admin) {
				this.companySign = this.value.companySign;
			}
		},
		'value.company_relationship_type': function(oldValue) {
			this.getCompanyRelationshipType();
		},
		selectedCompanyRelationshipType: function(oldValue) {
			if (this.selectedCompanyRelationshipType.value === 'child') {
				this.getAllParentCompanies();
			}
		},
		'value.letterHeader': function(oldValue) {
			if (this.checkCompanyTypeAllowance() || this.isLevel1Admin) {
				this.letterHeader = this.value.letterHeader;
			}
		},
		'value.damageAssessorCompanies': function(oldValue) {
			this.loadDamageAssessors();
		},
		'value.parentCompanies': function(oldValue) {
			this.loadDamageAssessors();
		},
		'value.logoPosition': function() {
			this.selectedLogoPosition = this.logoPositionOptions.find(option => option.value === this.value.logoPosition);
		},
	},
};
</script>

<style scoped>
.parent:hover .parent-hover\:show {
	background-color: #333;
	display: block;
	height: initial !important;
}
</style>
